// Pure phone-number validation helpers for the workflow phone screen.
// Verification only supports US numbers; these encode the US-region policy plus the
// premium-rate / unassigned / fictitious blocklists. NANP numbers are +1 NPA-NXX-XXXX
// (NPA = area code, digits 1-3; NXX = exchange, digits 4-6).

// US plus the five territories (American Samoa, Guam, Northern Mariana Islands, Puerto
// Rico, US Virgin Islands), which libphonenumber treats as their own +1 regions.
export const US_REGION_ISOS = ["us", "as", "gu", "mp", "pr", "vi"];

// Premium-rate area codes. Calls/texts to these bill at premium rates and are a fraud
// vector. Most are Caribbean NANP nations that also fail the US-region check, but
// blocking them here labels them precisely. 970/540 are deliberately NOT blocked —
// they're live geographic codes (CO/VA).
const BLOCKED_NPAS = [
  "242", "246", "264", "268", "284", "345", "441", "473", "649", "664", "721",
  "758", "767", "784", "809", "829", "849", "868", "869", "876", "900",
];
// Premium / special-use exchanges (NXX): 976 pay-per-call, 550 per reporter request.
const BLOCKED_NXXS = ["976", "550"];

// NANP area codes currently "Not in use" — used to reject unassigned area codes.
const NOT_IN_USE_AREA_CODES: ReadonlySet<string> = new Set([
  "221", "222", "232", "233", "237", "238", "241", "243", "244", "245", "247", "255",
  "258", "259", "261", "265", "266", "271", "275", "277", "278", "282", "288", "322",
  "333", "342", "344", "355", "356", "362", "366", "381", "387", "388", "389", "421",
  "422", "429", "433", "439", "444", "446", "449", "455", "456", "459", "466", "467",
  "476", "477", "481", "482", "488", "489", "535", "536", "538", "555", "556", "558",
  "565", "569", "576", "578", "583", "589", "625", "627", "632", "644", "648", "655",
  "663", "665", "666", "668", "677", "685", "687", "688", "722", "723", "733", "739",
  "741", "744", "752", "755", "756", "759", "761", "764", "766", "768", "776", "777",
  "783", "788", "789", "822", "827", "834", "836", "846", "889", "921", "922", "923",
  "926", "927", "932", "933", "935", "944", "946", "953", "955", "974", "976", "977",
  "987",
]);

// The 10-digit NANP national number from an E.164 string ("+19005551234" → "9005551234").
function getNanpNational(e164: string): string {
  const digits = e164.replace(/\D/g, "");
  return digits.startsWith("1") ? digits.slice(1) : digits;
}

// Premium-rate / special-use number; false until the area code + exchange are present.
export function isPremiumRateNumber(e164: string): boolean {
  const national = getNanpNational(e164);
  if (national.length < 6) return false;
  const npa = national.slice(0, 3);
  const nxx = national.slice(3, 6);
  return BLOCKED_NPAS.includes(npa) || BLOCKED_NXXS.includes(nxx);
}

// Unassigned area code ("Not in use" snapshot) or fictitious number; judged only on a
// complete 10-digit number. 555 is reserved for fiction except 555-0100..555-0199.
export function isUnassignedOrFictitiousNumber(e164: string): boolean {
  const national = getNanpNational(e164);
  if (national.length < 10) return false;
  const npa = national.slice(0, 3);
  const nxx = national.slice(3, 6);
  const line = Number(national.slice(6, 10));
  if (NOT_IN_USE_AREA_CODES.has(npa)) return true;
  if (nxx === "555" && (line < 100 || line > 199)) return true;
  return false;
}
